import React, { createContext, useContext, useState, useEffect } from 'react';
import { authService } from '../services/api';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (token) {
      // Check if we've already validated this token recently
      const lastAuthCheck = sessionStorage.getItem('lastAuthCheck');
      const timeSinceLastCheck = lastAuthCheck ? Date.now() - parseInt(lastAuthCheck) : Infinity;
      
      if (timeSinceLastCheck > 60000) { // Only check auth once per minute
        authService.getCurrentUser()
          .then(response => {
            setUser(response.data.user);
            sessionStorage.setItem('lastAuthCheck', Date.now().toString());
          })
          .catch(() => {
            localStorage.removeItem('token');
            sessionStorage.removeItem('lastAuthCheck');
          })
          .finally(() => {
            setLoading(false);
          });
      } else {
        // Use cached user data if available
        const cachedUser = sessionStorage.getItem('cachedUser');
        if (cachedUser) {
          setUser(JSON.parse(cachedUser));
        }
        setLoading(false);
      }
    } else {
      setLoading(false);
    }
  }, []);

  const login = async (email, password) => {
    try {
      const response = await authService.login(email, password);
      const { token, user } = response.data;
      
      localStorage.setItem('token', token);
      sessionStorage.setItem('cachedUser', JSON.stringify(user));
      sessionStorage.setItem('lastAuthCheck', Date.now().toString());
      setUser(user);
      
      return { success: true };
    } catch (error) {
      return { 
        success: false, 
        error: error.response?.data?.error || 'Login failed' 
      };
    }
  };

  const register = async (name, email, password) => {
    try {
      const response = await authService.register(name, email, password);
      const { token, user } = response.data;
      
      localStorage.setItem('token', token);
      sessionStorage.setItem('cachedUser', JSON.stringify(user));
      sessionStorage.setItem('lastAuthCheck', Date.now().toString());
      setUser(user);
      
      return { success: true };
    } catch (error) {
      return { 
        success: false, 
        error: error.response?.data?.error || 'Registration failed' 
      };
    }
  };

  const logout = () => {
    localStorage.removeItem('token');
    sessionStorage.removeItem('cachedUser');
    sessionStorage.removeItem('lastAuthCheck');
    setUser(null);
  };

  const value = {
    user,
    login,
    register,
    logout,
    loading
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};